
import { ExportOptions, ExportData } from "./types";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { format, startOfWeek, endOfWeek, getWeek, getYear, isSameWeek } from "date-fns";
import { fr } from "date-fns/locale";

/**
 * Structure pour les données d'un employé par semaine (format AVEM)
 */
interface AvemWeekData {
  employe: string;
  semaine: string;
  periodeDebut: string;
  periodeFin: string;
  jours: {
    [key: string]: {
      chantier: string;
      heureDebut: string;
      heureFin: string;
      totalHeures: number;
      commentaire: string;
    };
  };
  totalSemaine: number;
}

/**
 * Groupe les données par employé et par semaine selon le format AVEM
 */
const groupDataByEmployeeAndWeek = (data: ExportData[]): AvemWeekData[] => {
  const grouped = new Map<string, AvemWeekData>();
  
  data.forEach(entry => {
    const employe = entry.employé || '';
    const dateEntry = new Date(entry.date?.split('/').reverse().join('-') || '');
    
    if (isNaN(dateEntry.getTime())) return;
    
    const weekStart = startOfWeek(dateEntry, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(dateEntry, { weekStartsOn: 1 });
    const weekNumber = getWeek(dateEntry, { weekStartsOn: 1 });
    const year = getYear(dateEntry);
    
    const weekKey = `${employe}-${year}-W${weekNumber.toString().padStart(2, '0')}`;
    
    if (!grouped.has(weekKey)) {
      grouped.set(weekKey, {
        employe,
        semaine: `Semaine ${weekNumber} - ${year}`,
        periodeDebut: format(weekStart, 'dd/MM/yyyy'),
        periodeFin: format(weekEnd, 'dd/MM/yyyy'),
        jours: {},
        totalSemaine: 0
      });
    }
    
    const weekData = grouped.get(weekKey)!;
    const jourKey = format(dateEntry, 'EEEE dd/MM', { locale: fr });
    
    // Calculer les heures
    let heures = 0;
    if (entry.entrée && entry.sortie) {
      const [heureDebut, minDebut] = entry.entrée.split(':').map(Number);
      const [heureFin, minFin] = entry.sortie.split(':').map(Number);
      heures = (heureFin + minFin / 60) - (heureDebut + minDebut / 60);
    }
    
    weekData.jours[jourKey] = {
      chantier: entry.chantier || '',
      heureDebut: entry.entrée || '',
      heureFin: entry.sortie || '',
      totalHeures: heures,
      commentaire: entry.commentaire || ''
    };
    
    weekData.totalSemaine += heures;
  });
  
  return Array.from(grouped.values()).sort((a, b) => a.employe.localeCompare(b.employe));
};

/**
 * Exporte des données au format PDF selon le format AVEM
 */
export const exportToPdf = async (
  data: ExportData[],
  options: ExportOptions = {}
): Promise<Blob> => {
  const doc = new jsPDF();
  
  if (!data || data.length === 0) {
    doc.text("Aucune donnée disponible", 20, 20);
    return doc.output("blob");
  }
  
  // Grouper les données par employé et semaine
  const weeklyData = groupDataByEmployeeAndWeek(data);
  
  let yPosition = 20;
  
  weeklyData.forEach((employeeWeek, index) => {
    // Nouvelle page pour chaque employé (sauf le premier)
    if (index > 0) {
      doc.addPage();
      yPosition = 20;
    }
    
    // En-tête de la feuille
    doc.setFontSize(16);
    doc.text(`FEUILLE DE POINTAGE - ${employeeWeek.employe.toUpperCase()}`, 14, yPosition);
    yPosition += 10;
    
    doc.setFontSize(12);
    doc.text(`${employeeWeek.semaine} (du ${employeeWeek.periodeDebut} au ${employeeWeek.periodeFin})`, 14, yPosition);
    yPosition += 15;
    
    // Tableau des pointages
    const tableData: string[][] = [];
    const jours = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    
    jours.forEach(jour => {
      const jourData = Object.entries(employeeWeek.jours).find(([key]) => 
        key.toLowerCase().startsWith(jour.toLowerCase())
      );
      
      if (jourData) {
        const [jourComplet, donnees] = jourData;
        tableData.push([
          jourComplet,
          donnees.chantier,
          donnees.heureDebut,
          donnees.heureFin,
          donnees.totalHeures > 0 ? `${donnees.totalHeures.toFixed(1)}h` : '',
          donnees.commentaire
        ]);
      } else {
        // Ligne vide pour les jours sans pointage
        tableData.push([jour, '', '', '', '', '']);
      }
    });
    
    // Ligne de total
    tableData.push([
      'TOTAL SEMAINE',
      '',
      '',
      '',
      `${employeeWeek.totalSemaine.toFixed(1)}h`,
      ''
    ]);
    
    autoTable(doc, {
      head: [['Jour', 'Chantier', 'Arrivée', 'Départ', 'Total', 'Commentaires']],
      body: tableData,
      startY: yPosition,
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [189, 30, 40], // Rouge AVEM
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      bodyStyles: {
        textColor: [0, 0, 0]
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Jour
        1: { cellWidth: 40 }, // Chantier
        2: { cellWidth: 20 }, // Arrivée
        3: { cellWidth: 20 }, // Départ
        4: { cellWidth: 20 }, // Total
        5: { cellWidth: 50 }  // Commentaires
      },
      didParseCell: (data) => {
        // Mettre en évidence la ligne de total
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fillColor = [240, 240, 240];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    
    // Signature et validation
    const finalY = (doc as any).lastAutoTable.finalY + 20;
    
    doc.setFontSize(10);
    doc.text('Signature employé:', 14, finalY);
    doc.text('Signature responsable:', 105, finalY);
    
    // Ligne pour signature
    doc.line(45, finalY + 2, 90, finalY + 2);
    doc.line(145, finalY + 2, 190, finalY + 2);
  });
  
  // Pied de page avec informations
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Généré le ${format(new Date(), "dd/MM/yyyy à HH:mm", { locale: fr })} - Page ${i}/${pageCount}`,
      14,
      doc.internal.pageSize.height - 10
    );
  }
  
  return doc.output("blob");
};
